// src/components/modal/AppModal.tsx
import React, { useRef, useEffect } from 'react';
import { Modal, View, Text, ScrollView, Pressable, KeyboardAvoidingView, Keyboard, StyleSheet } from 'react-native';
import { X } from 'lucide-react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import { isIOS } from '@/lib/platform';
import { useKeyboardVisible } from '@/lib/useKeyboardVisible';
import { modalTokens } from './modal-tokens';
import { getModalSurfaceStyle } from './modal-layout';
import { KeyboardAwareBody } from './KeyboardAwareBody';

interface AppModalProps {
  visible: boolean;
  onClose: () => void;
  title?: string;
  headerRight?: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: number;
  scroll?: boolean;
  /**
   * When this value CHANGES to something truthy, the modal body scrolls to the
   * end. Use it to reveal an inline form/input that a footer action just opened
   * (e.g. "Request Info") so the user doesn't have to hunt for it below the fold.
   */
  scrollToEndSignal?: unknown;
  children: React.ReactNode;
}

export function AppModal({
  visible,
  onClose,
  title,
  headerRight,
  footer,
  maxWidth,
  scroll = true,
  scrollToEndSignal,
  children,
}: AppModalProps) {
  const colors = useThemeColors();
  const insets = useSafeAreaInsets();
  const scrollRef = useRef<ScrollView>(null);
  const pendingScrollRef = useRef(false);
  const keyboardVisible = useKeyboardVisible();

  /**
   * Backdrop press: put the keyboard away FIRST, close only once it is gone.
   *
   * This used to call `Keyboard.dismiss()` and `onClose()` together, so the one
   * gesture that hid the keyboard also destroyed the modal and everything typed
   * into it. There was no way to stop typing and read the form you had just
   * filled in — half of the "modals are unusable on mobile" complaint, and it
   * affected every modal already migrated onto this primitive.
   */
  const handleBackdropPress = () => {
    if (keyboardVisible) {
      Keyboard.dismiss();
      return;
    }
    onClose();
  };

  useEffect(() => {
    if (scrollToEndSignal) {
      // Arm a scroll-to-end that fires when the newly-revealed content (the
      // form + its input) has actually laid out — see onContentSizeChange below.
      // A fixed delay races the input's layout and under-scrolls, so we scroll
      // on the size change and keep a longer timeout only as a fallback.
      pendingScrollRef.current = true;
      const t = setTimeout(() => {
        if (pendingScrollRef.current) scrollRef.current?.scrollToEnd({ animated: true });
      }, 350);
      return () => clearTimeout(t);
    }
  }, [scrollToEndSignal]);

  const surfaceStyle = getModalSurfaceStyle({ maxWidth, background: colors.surface.primary });

  const bodyPadding = {
    paddingHorizontal: modalTokens.body.paddingHorizontal,
    paddingTop: modalTokens.body.paddingTop,
    paddingBottom: modalTokens.body.paddingBottom,
  };
  const hasHeader = Boolean(title) || Boolean(headerRight);

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      {/*
        THE BACKDROP IS A SIBLING BEHIND THE CARD, NOT AN ANCESTOR AROUND IT.
        
        It used to wrap everything, which meant a tap on the card fell through
        and closed the modal — so the card was made a `Pressable` with a no-op
        `onPress` to intercept. That fixed taps and broke scrolling: on React
        Native a Pressable claims the responder on touch START, while a
        ScrollView deliberately waits and claims on MOVE so taps can still reach
        its children. With the body's ScrollView nested inside that Pressable,
        the Pressable always won and the drag never reached the scroller.
        Reported from production as "only a tiny corner reacts to the scroll
        gesture; any movement on the window is non-responsive".
        
        Layered this way there is nothing to intercept: the card is on top, so a
        tap on it cannot reach the backdrop, and the ScrollView is free to claim
        its own gestures. Every modal in the app is built on this primitive.
      */}
      <View
        style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          paddingHorizontal: modalTokens.screenMargin,
          paddingTop: insets.top + modalTokens.screenMargin,
          paddingBottom: insets.bottom + modalTokens.screenMargin,
        }}
      >
        <Pressable
          accessibilityLabel={keyboardVisible ? 'Dismiss keyboard' : 'Close modal'}
          accessibilityRole="button"
          onPress={handleBackdropPress}
          style={[StyleSheet.absoluteFill, { backgroundColor: modalTokens.backdropColor }]}
        />
        {/* 'height' on Android matches the repo convention (ProfileEditModal, PitchDeckAnalysisModal) */}
        <KeyboardAvoidingView
          behavior={isIOS ? 'padding' : 'height'}
          pointerEvents="box-none"
          style={{ flex: 1, width: '100%', alignItems: 'center', justifyContent: 'center' }}
        >
          {/* A plain View. Nothing here needs to intercept a gesture — the
              backdrop is behind the card, not around it. */}
          <View style={surfaceStyle}>
            {hasHeader && (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  paddingHorizontal: modalTokens.header.paddingHorizontal,
                  paddingTop: modalTokens.header.paddingTop,
                  paddingBottom: modalTokens.header.paddingBottom,
                  borderBottomWidth: 1,
                  borderBottomColor: colors.border.light,
                }}
              >
                {title ? (
                  <Text numberOfLines={1} style={{ flex: 1, fontSize: 18, fontWeight: '700', color: colors.text.primary }}>
                    {title}
                  </Text>
                ) : (
                  <View style={{ flex: 1 }} />
                )}
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                  {headerRight}
                  {/* The circle View owns the size + centering; AnimatedPressable's
                      transform wrapper can't fill via flex, so centering here keeps
                      the X centred instead of clipped to a "^" at the top. */}
                  <View style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: colors.surface.secondary, alignItems: 'center', justifyContent: 'center' }}>
                    <AnimatedPressable onPress={onClose} accessibilityLabel="Close" hitSlop={8}>
                      <X size={20} color={colors.text.primary} strokeWidth={2.5} />
                    </AnimatedPressable>
                  </View>
                </View>
              </View>
            )}

            {scroll ? (
              // The body scrolls a focused input into view — see
              // KeyboardAwareBody for why that behaviour lives in its own
              // component rather than inline here.
              <KeyboardAwareBody
                ref={scrollRef}
                contentContainerStyle={bodyPadding}
                onContentSizeChange={() => {
                  // Fires once the revealed form (incl. its input) has laid out.
                  if (pendingScrollRef.current) {
                    pendingScrollRef.current = false;
                    scrollRef.current?.scrollToEnd({ animated: true });
                  }
                }}
              >
                {children}
              </KeyboardAwareBody>
            ) : (
              <View style={bodyPadding}>{children}</View>
            )}

            {footer && (
              <View
                style={{
                  paddingHorizontal: modalTokens.footer.paddingHorizontal,
                  paddingTop: modalTokens.footer.paddingTop,
                  paddingBottom: modalTokens.footer.paddingBottom,
                  borderTopWidth: 1,
                  borderTopColor: colors.border.light,
                }}
              >
                {footer}
              </View>
            )}
          </View>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );
}

/**
 * Action button row. Buttons wrap onto multiple rows instead of scrolling
 * (users shouldn't have to scroll to reach an action). Each ModalAction grows
 * to fill the row: a single 2-up grid on mobile, an even wrapping row on web.
 */
function Actions({ children }: { children: React.ReactNode }) {
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: modalTokens.actionGap }}>
      {children}
    </View>
  );
}

AppModal.Actions = Actions;
